import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const SiteContext = createContext(null);

export function SiteProvider({ children }) {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [activeSite, setActiveSite] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/sites");
      setSites(data);
      if (data.length) {
        const stored = localStorage.getItem("active_site_id");
        const found = data.find((s) => s.id === stored);
        setActiveSite(found || data[0]);
      } else {
        setActiveSite(null);
      }
    } catch {
      // 401 here just means the session expired or the user logged out;
      // AuthContext will route them back to /login. Reset state quietly.
      setSites([]);
      setActiveSite(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user !== false) {
      refresh();
    } else if (user === false) {
      // Logged out → clear cached sites so background sync ticks stop hitting the API.
      setSites([]);
      setActiveSite(null);
      setLoading(false);
    }
  }, [user, refresh]);

  const selectSite = useCallback((site) => {
    setActiveSite(site);
    if (site) localStorage.setItem("active_site_id", site.id);
  }, []);

  const value = useMemo(
    () => ({ sites, activeSite, selectSite, loading, refresh }),
    [sites, activeSite, selectSite, loading, refresh]
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export const useSite = () => useContext(SiteContext);
